import { forwardRef, type FormEventHandler, type ReactNode, type RefObject } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "../lib/cn";
import { IconButton } from "./IconButton";

export type DialogSize = "sm" | "md" | "lg";

export interface DialogProps {
  open: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
  description?: ReactNode;
  footer?: ReactNode;
  headerLeading?: ReactNode;
  initialFocusRef?: RefObject<HTMLElement | null>;
  closeDisabled?: boolean;
  closeLabel?: string;
  size?: DialogSize;
  className?: string;
  bodyClassName?: string;
  footerClassName?: string;
  formId?: string;
  onSubmit?: FormEventHandler<HTMLFormElement>;
}

const sizeClasses: Record<DialogSize, string> = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
};

export function Dialog({
  open,
  title,
  children,
  onClose,
  description,
  footer,
  headerLeading,
  initialFocusRef,
  closeDisabled = false,
  closeLabel = "Close dialog",
  size = "md",
  className,
  bodyClassName,
  footerClassName,
  formId,
  onSubmit,
}: DialogProps) {
  const body = (
    <div className={cn("min-h-0 flex-1 overflow-y-auto px-5 py-4", bodyClassName)}>{children}</div>
  );
  const footerContent = footer ? (
    <div
      className={cn(
        "flex shrink-0 flex-wrap justify-end gap-2 border-t border-border px-5 py-4",
        footerClassName,
      )}
    >
      {footer}
    </div>
  ) : null;

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !closeDisabled) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-70 bg-overlay backdrop-blur-[2px]" />
        <DialogPrimitive.Content
          data-escape-key-handled="true"
          onEscapeKeyDown={(event) => {
            // A view inside the dialog that Escape steps back from (`data-escape-steps-back`)
            // handles the key itself: the dialog stays open.
            const target = event.target instanceof Element ? event.target : null;
            if (closeDisabled || target?.closest("[data-escape-steps-back]"))
              event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (closeDisabled) event.preventDefault();
          }}
          onOpenAutoFocus={(event) => {
            if (!initialFocusRef?.current) return;
            event.preventDefault();
            initialFocusRef.current.focus();
          }}
          className={cn(
            "fixed left-1/2 top-1/2 z-70 flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden",
            "rounded-dialog border border-border bg-surface shadow-dialog",
            sizeClasses[size],
            className,
          )}
        >
          <div className="flex shrink-0 items-start gap-3 border-b border-border px-5 py-4">
            {headerLeading}
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="text-base font-semibold text-primary">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-0.5 text-xs leading-5 text-muted">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
            <DialogPrimitive.Close asChild disabled={closeDisabled}>
              <IconButton label={closeLabel} disabled={closeDisabled} className="text-muted">
                <X className="h-4 w-4" aria-hidden />
              </IconButton>
            </DialogPrimitive.Close>
          </div>

          {onSubmit ? (
            <DialogForm id={formId} onSubmit={onSubmit}>
              {body}
              {footerContent}
            </DialogForm>
          ) : (
            <>
              {body}
              {footerContent}
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

const DialogForm = forwardRef<
  HTMLFormElement,
  { id?: string; onSubmit: FormEventHandler<HTMLFormElement>; children: ReactNode }
>(function DialogForm({ id, onSubmit, children }, ref) {
  return (
    <form ref={ref} id={id} onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
      {children}
    </form>
  );
});
